/**
 * DIFF-3 seed — carrier trust scores, alerts, and vetting history.
 *
 * Uses 10 carriers from the existing EXPANDED_CARRIERS list (first 10) so we
 * don't need to fabricate carriers. Trust scores span all four tiers so the
 * demo shows realistic distribution.
 */
import { DEMO_TENANT_ID } from './tenantDefaults';

const T = DEMO_TENANT_ID;
const iso = (days = 0, hours = 0) => new Date(Date.now() - (days * 86400000 + hours * 3600000)).toISOString();

/**
 * Each row deliberately exercises a different combination of signals so the
 * UI shows: trusted, preferred, watchlist, restricted tiers.
 */
const CARRIERS = [
  { id: 'carrier-od',        name: 'Old Dominion Freight Line', scac: 'ODFL', mc: 'MC-123456', dot: 'DOT-1542821' },
  { id: 'carrier-xpo',       name: 'XPO Logistics',             scac: 'CNWY', mc: 'MC-165267', dot: 'DOT-1061680' },
  { id: 'carrier-saia',      name: 'Saia LTL Freight',          scac: 'SAIA', mc: 'MC-198321', dot: 'DOT-1342988' },
  { id: 'carrier-estes',     name: 'Estes Express Lines',       scac: 'EXLA', mc: 'MC-107885', dot: 'DOT-109858'  },
  { id: 'carrier-abf',       name: 'ABF Freight',               scac: 'ABFS', mc: 'MC-082357', dot: 'DOT-082357'  },
  { id: 'carrier-schneider', name: 'Schneider',                 scac: 'SCNN', mc: 'MC-264184', dot: 'DOT-264184'  },
  { id: 'carrier-jbhunt',    name: 'J.B. Hunt',                 scac: 'JBHT', mc: 'MC-150301', dot: 'DOT-150301'  },
  { id: 'carrier-swft',      name: 'Swift Transportation',      scac: 'SWFT', mc: 'MC-381002', dot: 'DOT-381002'  },
  { id: 'carrier-usat',      name: 'USA Truck',                 scac: 'USAT', mc: 'MC-117490', dot: 'DOT-117490'  },
  { id: 'carrier-ryder',     name: 'Ryder System',              scac: 'RYDS', mc: 'MC-060001', dot: 'DOT-060001'  },
];

function score(carrier, i) {
  // Deterministic variety: two trusted (95, 91), three preferred (83, 78, 72),
  // two watchlist (65, 58), two restricted (48, 41), one "never vetted" (last).
  const profiles = [
    { score: 95, tier: 'trusted',    fmcsa: { authorityStatus: 'active', safetyRating: 'satisfactory', insuranceStatus: 'valid',          insuranceExpiryDays: 180, outOfServiceRate: 1.2, csaScore: 64, mock: true, retrievedAt: iso(0, 2) }, otd: 98, check: 96, comm: 94, dbf: 0 },
    { score: 91, tier: 'trusted',    fmcsa: { authorityStatus: 'active', safetyRating: 'satisfactory', insuranceStatus: 'valid',          insuranceExpiryDays: 220, outOfServiceRate: 0.8, csaScore: 58, mock: true, retrievedAt: iso(0, 3) }, otd: 95, check: 94, comm: 90, dbf: 0 },
    { score: 83, tier: 'preferred',  fmcsa: { authorityStatus: 'active', safetyRating: 'satisfactory', insuranceStatus: 'valid',          insuranceExpiryDays: 110, outOfServiceRate: 1.6, csaScore: 52, mock: true, retrievedAt: iso(0, 4) }, otd: 91, check: 88, comm: 85, dbf: 0 },
    { score: 78, tier: 'preferred',  fmcsa: { authorityStatus: 'active', safetyRating: 'satisfactory', insuranceStatus: 'valid',          insuranceExpiryDays: 85,  outOfServiceRate: 2.0, csaScore: 48, mock: true, retrievedAt: iso(0, 5) }, otd: 88, check: 82, comm: 80, dbf: 0 },
    { score: 72, tier: 'preferred',  fmcsa: { authorityStatus: 'active', safetyRating: 'satisfactory', insuranceStatus: 'expiring_soon',  insuranceExpiryDays: 24,  outOfServiceRate: 2.4, csaScore: 46, mock: true, retrievedAt: iso(0, 6) }, otd: 85, check: 78, comm: 76, dbf: 0 },
    { score: 65, tier: 'watchlist',  fmcsa: { authorityStatus: 'active', safetyRating: 'conditional',  insuranceStatus: 'valid',          insuranceExpiryDays: 60,  outOfServiceRate: 3.4, csaScore: 38, mock: true, retrievedAt: iso(0, 7) }, otd: 80, check: 70, comm: 68, dbf: 10 },
    { score: 58, tier: 'watchlist',  fmcsa: { authorityStatus: 'active', safetyRating: 'satisfactory', insuranceStatus: 'expiring_soon',  insuranceExpiryDays: 12,  outOfServiceRate: 4.1, csaScore: 36, mock: true, retrievedAt: iso(0, 8) }, otd: 76, check: 68, comm: 65, dbf: 20 },
    { score: 48, tier: 'restricted', fmcsa: { authorityStatus: 'active', safetyRating: 'conditional',  insuranceStatus: 'expired',        insuranceExpiryDays: -8,  outOfServiceRate: 5.2, csaScore: 28, mock: true, retrievedAt: iso(0, 9) }, otd: 74, check: 62, comm: 58, dbf: 35, flagged: [{ loadId: 'load-00091', loadNumber: 'WC-10791', signals: ['pickup_driver_mismatch', 'mc_mismatch_on_delivery_doc'] }] },
    { score: 41, tier: 'restricted', fmcsa: { authorityStatus: 'inactive', safetyRating: 'unsatisfactory', insuranceStatus: 'expired',    insuranceExpiryDays: -32, outOfServiceRate: 6.8, csaScore: 22, mock: true, retrievedAt: iso(0, 10) }, otd: 58, check: 50, comm: 42, dbf: 55, flagged: [{ loadId: 'load-00092', loadNumber: 'WC-10792', signals: ['phone_redirects_to_different_company'] }, { loadId: 'load-00093', loadNumber: 'WC-10793', signals: ['equipment_inconsistency'] }] },
    null, // last one "not vetted" so the badge shows "Not vetted"
  ];
  const p = profiles[i];
  if (!p) return null;
  const alerts = [];
  if (p.fmcsa.authorityStatus !== 'active') alerts.push({ severity: 'critical', type: 'authority_inactive', message: `FMCSA authority status: ${p.fmcsa.authorityStatus}`, detectedAt: iso(0, 10) });
  if (p.fmcsa.safetyRating !== 'satisfactory') alerts.push({ severity: p.fmcsa.safetyRating === 'unsatisfactory' ? 'critical' : 'warning', type: 'safety_rating', message: `Safety rating: ${p.fmcsa.safetyRating}`, detectedAt: iso(0, 9) });
  if (p.fmcsa.insuranceStatus === 'expired') alerts.push({ severity: 'critical', type: 'insurance_expired', message: `Insurance expired ${Math.abs(p.fmcsa.insuranceExpiryDays)}d ago`, detectedAt: iso(0, 8) });
  else if (p.fmcsa.insuranceStatus === 'expiring_soon') alerts.push({ severity: 'warning', type: 'insurance_expiring', message: `Insurance expires in ${p.fmcsa.insuranceExpiryDays}d`, detectedAt: iso(0, 7) });
  if (p.dbf >= 35) alerts.push({ severity: p.dbf >= 60 ? 'critical' : 'warning', type: 'double_broker_fingerprint', message: `${(p.flagged || []).reduce((s, f) => s + f.signals.length, 0)} re-broker signal(s)`, signals: (p.flagged || []).flatMap(f => f.signals), detectedAt: iso(0, 6) });

  const behaviorBase = Math.round(p.otd * 0.5 + p.check * 0.25 + p.comm * 0.25);

  return {
    id: 'trust-' + (i + 1).toString().padStart(3, '0'),
    tenantId: T,
    carrierId: carrier.id, carrierName: carrier.name, scac: carrier.scac,
    mcNumber: carrier.mc, dotNumber: carrier.dot,
    score: p.score, tier: p.tier,
    fmcsa: p.fmcsa,
    behavior: { otdPct: p.otd, deliveredCount: 30 + i * 3, checkInCompliance: p.check, commScore: p.comm, loadCount: 40 + i * 4, composite: behaviorBase },
    doubleBrokerFingerprint: { score: p.dbf, signalCount: (p.flagged || []).reduce((s, f) => s + f.signals.length, 0), flaggedLoads: p.flagged || [] },
    alerts,
    computedAt: iso(0, 2),
    isDeleted: false, createdAt: iso(2), updatedAt: iso(0, 2), version: 3, source: 'carrier_vetting_engine',
  };
}

export const SEED_CARRIER_TRUST_SCORES = CARRIERS.map((c, i) => score(c, i)).filter(Boolean);

// Expand alerts into their own rows so the feed has distinct items
export const SEED_CARRIER_ALERTS = SEED_CARRIER_TRUST_SCORES.flatMap(r =>
  (r.alerts || []).map((a, j) => ({
    id: `alert-${r.carrierId}-${j + 1}`,
    tenantId: T,
    carrierId: r.carrierId, carrierName: r.carrierName,
    severity: a.severity, type: a.type, message: a.message,
    signals: a.signals || null,
    status: 'open',
    detectedAt: a.detectedAt,
    isDeleted: false, createdAt: a.detectedAt, updatedAt: a.detectedAt, version: 1, source: 'carrier_vetting_engine',
  }))
);

// History trail — 14 runs over the past 21 days per carrier showing drift.
export const SEED_CARRIER_VETTING_HISTORY = SEED_CARRIER_TRUST_SCORES.flatMap((r, idx) => {
  const runs = [];
  const target = r.score;
  for (let i = 0; i < 14; i++) {
    const jitter = (idx % 3 === 0 ? -1 : 1) * ((14 - i) * 0.6);
    const pastScore = Math.max(15, Math.min(100, Math.round(target + jitter)));
    runs.push({
      id: `hist-${r.carrierId}-${i + 1}`,
      tenantId: T,
      carrierId: r.carrierId, carrierName: r.carrierName,
      score: pastScore,
      tier: pastScore >= 85 ? 'trusted' : pastScore >= 70 ? 'preferred' : pastScore >= 55 ? 'watchlist' : 'restricted',
      fmcsaAuthority: r.fmcsa.authorityStatus,
      fmcsaSafety: r.fmcsa.safetyRating,
      insuranceStatus: r.fmcsa.insuranceStatus,
      behaviorComposite: r.behavior.composite,
      dbfScore: r.doubleBrokerFingerprint.score,
      runAt: iso(21 - Math.round(i * 1.5)),
      isDeleted: false, createdAt: iso(21 - Math.round(i * 1.5)), updatedAt: iso(21 - Math.round(i * 1.5)), version: 1, source: 'carrier_vetting_engine',
    });
  }
  return runs;
});

export const CARRIER_VETTING_SEED_SUMMARY = {
  carrier_trust_scores: SEED_CARRIER_TRUST_SCORES.length,
  carrier_alerts: SEED_CARRIER_ALERTS.length,
  carrier_vetting_history: SEED_CARRIER_VETTING_HISTORY.length,
};
